// src/services/storyReactionService.js
import { db } from '../config/firebase';
import { 
  collection, doc, addDoc, updateDoc, deleteDoc, 
  getDocs, getDoc, query, where, orderBy,
  increment, serverTimestamp 
} from 'firebase/firestore';
import { firestoreService } from './firestoreService';

class StoryReactionService {
  
  // Create a reaction to a story or specific media in story
  async createReaction(albumId, storyId, reactionData, userId, userInfo = {}) {
    try {
      console.log('💖 Creating story reaction:', { albumId, storyId, reactionData, userId });
      
      const reactionRef = collection(db, 'pb-album-reactions');
      
      const reaction = {
        albumId,
        storyId,
        mediaId: reactionData.mediaId || null,
        userId,
        type: reactionData.type,
        content: reactionData.content || null,
        timestamp: serverTimestamp(),
        
        // Include user information
        userName: userInfo.displayName || userInfo.name || 'Anonymous',
        userAvatar: userInfo.photoURL || userInfo.avatar || null,
        userEmail: userInfo.email || null, // Optional, for admin purposes
        
        // Additional data for different reaction types
        ...(reactionData.audioUrl && { audioUrl: reactionData.audioUrl }),
        ...(reactionData.videoUrl && { videoUrl: reactionData.videoUrl }),
        ...(reactionData.photoUrl && { photoUrl: reactionData.photoUrl }),
        ...(reactionData.fileName && { fileName: reactionData.fileName })
      };

      const docRef = await addDoc(reactionRef, reaction);
      
      // Update story reaction counts
      await this.updateStoryReactionCounts(albumId, storyId, reactionData.type, 1);
      
      return { id: docRef.id, ...reaction };
      
    } catch (error) {
      console.error('💖 Error creating story reaction:', error);
      throw error;
    }
  }

  // Get all reactions for a story
  async getStoryReactions(albumId, storyId) {
    try {
      const reactionsRef = collection(db, 'pb-album-reactions');
      const q = query(
        reactionsRef,
        where('albumId', '==', albumId),
        where('storyId', '==', storyId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
    } catch (error) {
      console.error('💖 Error getting story reactions:', error);
      return [];
    }
  }

  // Get reactions for specific media within a story
  async getMediaReactionsInStory(albumId, storyId, mediaId) {
    try {
      const reactionsRef = collection(db, 'pb-album-reactions');
      const q = query(
        reactionsRef,
        where('albumId', '==', albumId),
        where('storyId', '==', storyId),
        where('mediaId', '==', mediaId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
    } catch (error) {
      console.error('💖 Error getting media reactions in story:', error);
      return [];
    }
  }

  // Toggle like reaction (like/unlike)
  async toggleLikeReaction(albumId, storyId, mediaId, userId, userInfo = {}) {
    try {
      // Check if user already liked this
      const reactionsRef = collection(db, 'pb-album-reactions');
      const q = query(
        reactionsRef,
        where('albumId', '==', albumId),
        where('storyId', '==', storyId),
        where('mediaId', '==', mediaId),
        where('userId', '==', userId),
        where('type', '==', 'like')
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Create like reaction with user info
        return await this.createReaction(albumId, storyId, {
          type: 'like',
          mediaId
        }, userId, userInfo);
      } else {
        // Remove like reaction
        const likeDoc = snapshot.docs[0];
        await deleteDoc(likeDoc.ref);
        
        // Update counts
        await this.updateStoryReactionCounts(albumId, storyId, 'like', -1);
        
        return { removed: true, id: likeDoc.id };
      }
      
    } catch (error) {
      console.error('💖 Error toggling like reaction:', error);
      throw error;
    }
  }

  // Add comment reaction
  async addComment(albumId, storyId, mediaId, comment, userId, userInfo = {}) {
    return await this.createReaction(albumId, storyId, {
      type: 'comment',
      mediaId,
      content: comment
    }, userId, userInfo);
  }

  // Add audio reaction (using existing upload pattern)
  async addAudioReaction(albumId, storyId, mediaId, audioFile, userId, userInfo = {}) {
    try {
      const uploadResult = await firestoreService.uploadFile(
        audioFile, 
        `pb-${albumId}/story-reactions/audio/${storyId}`
      );
      
      return await this.createReaction(albumId, storyId, {
        type: 'audio',
        mediaId,
        audioUrl: uploadResult.url,
        fileName: uploadResult.fileName,
        duration: Math.round(audioFile.size / 16000)
      }, userId, userInfo);
      
    } catch (error) {
      console.error('💖 Error adding audio reaction:', error);
      throw error;
    }
  }

  // Add photo reaction
  async addPhotoReaction(albumId, storyId, mediaId, photoFile, userId, userInfo = {}) {
    try {
      const uploadResult = await firestoreService.uploadFile(
        photoFile,
        `pb-${albumId}/story-reactions/photos/${storyId}`
      );
      
      return await this.createReaction(albumId, storyId, {
        type: 'photo',
        mediaId,
        photoUrl: uploadResult.url,
        fileName: uploadResult.fileName
      }, userId, userInfo);
      
    } catch (error) {
      console.error('💖 Error adding photo reaction:', error);
      throw error;
    }
  }
  // Update story reaction counts (stored in story document)
  async updateStoryReactionCounts(albumId, storyId, reactionType, increment_value) {
    try {
      const storyRef = doc(db, `pb-albums/${albumId}/stories`, storyId);
      
      const updateData = {
        [`reactionCounts.${reactionType}`]: increment(increment_value),
        'reactionCounts.total': increment(increment_value),
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(storyRef, updateData);
      
    } catch (error) {
      console.error('💖 Error updating story reaction counts:', error);
    }
  }

  // Delete a reaction
  async deleteReaction(reactionId, albumId, storyId, reactionType) {
    try {
      const reactionRef = doc(db, 'pb-album-reactions', reactionId);
      await deleteDoc(reactionRef);
      
      // Update counts
      await this.updateStoryReactionCounts(albumId, storyId, reactionType, -1);
      
      return true;
    } catch (error) {
      console.error('💖 Error deleting reaction:', error);
      throw error;
    }
  }

  // Get user's reactions to a story (to check what they've already reacted to)
  async getUserStoryReactions(albumId, storyId, userId) {
    try {
      const reactionsRef = collection(db, 'pb-album-reactions');
      const q = query(
        reactionsRef,
        where('albumId', '==', albumId),
        where('storyId', '==', storyId),
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
    } catch (error) {
      console.error('💖 Error getting user story reactions:', error);
      return [];
    }
  }
}

export default new StoryReactionService();